import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import {
  Archive,
  ArrowLeft,
  ArrowLeftRight,
  Ban,
  Download,
  FileSpreadsheet,
  KeyRound,
  Layers,
  LockOpen,
  Pencil,
  Plus,
  Power,
  RotateCcw,
  Trash2,
  TriangleAlert,
  UserPlus,
} from 'lucide-react'
import {
  getV1MeRetrieveQueryOptions,
  getV1ClassesListQueryKey,
  getV1ClassesRetrieveQueryKey,
  getV1ClassesStudentsRetrieveQueryKey,
  getV1SchoolsListQueryKey,
  useV1ClassesArchiveCreate,
  useV1ClassesBulkCascadeDeleteCreate,
  useV1ClassesBulkDisableCreate,
  useV1ClassesDeleteCreate,
  useV1ClassesRestoreCreate,
  useV1ClassesRetrieve,
  useV1ClassesStudentsBlockCreate,
  useV1ClassesStudentsCreate,
  useV1ClassesStudentsDisableCreate,
  useV1ClassesStudentsRestoreCreate,
  useV1ClassesStudentsRetrieve,
  useV1ClassesStudentsSheetCreate,
  useV1ClassesStudentsSheetDestroy,
  useV1ClassesStudentsTransferCreate,
  useV1ClassesStudentsUnblockCreate,
  useV1MeRetrieve,
  useV1StudentsPasswordResetCreate,
  v1ClassesStudentsDestroy,
  v1ClassesStudentsSheetRetrieve,
} from '@/api/client'
import type { ActivityEnum, Me, StateEnum, StudentRosterRow } from '@/api/model'
import { V1ClassesStudentsRetrieveState } from '@/api/model'
import { Pager } from '@/components/pager'
import { keepPreviousPage } from '@/lib/keep-previous-page'
import { PageHeader } from '@/components/page-header'
import { StatusBadge, type StatusTone } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/admin-accounts/confirm-dialog'
import { BulkScopeActions } from '@/components/bulk/bulk-scope-actions'
import { DeleteClassDialog } from '@/components/classes/class-actions-dialog'
import { DeleteStudentDialog } from '@/components/classes/delete-student-dialog'
import { ResetPasswordDialog } from '@/components/classes/reset-password-dialog'
import { resetErrorCopy } from '@/components/classes/reset-password-copy'
import { formatExpiry } from '@/lib/temporary-password'
import { RestoreStudentDialog } from '@/components/classes/restore-student-dialog'
import { TransferStudentDialog } from '@/components/classes/transfer-student-dialog'
import { AvatarPortrait } from '@/components/profile/dicebear-avatar'
import { avatarSeed } from '@/lib/avatar-catalog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ClassForm } from '@/components/classes/class-form'
import { ModuleConfigCard } from '@/components/modules/module-config-card'
import { StudentModulesDialog } from '@/components/modules/student-modules-dialog'
import { ClassCoordinatorsCard } from '@/components/teachers/class-coordinators-card'
import { studentLogin, studentLoginValue, studentLoginWireFields } from '@/lib/student-login'

export const Route = createFileRoute('/_panel/klasy/$klasaId')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && !['teacher', 'admin', 'superadmin'].includes(me.data.role)) {
      throw redirect({ to: '/' })
    }
  },
  component: KlasaDetailPage,
})

const stateTone: Record<StateEnum, StatusTone> = {
  active: 'green',
  archived: 'zinc',
  deleted: 'red',
}

type Credentials = { login: string; password: string; expiresAt?: string }

// TR-1100/1101 scoped roster state filter (feature 011). 'active' sends no `state` param
// (default: active + blocked); the other two map to the generated enum.
type StudentRosterStateFilter = 'active' | 'disabled' | 'deleted'

const ROSTER_PAGE_SIZE = 25

const rosterStateOptions: { value: StudentRosterStateFilter; label: string }[] = [
  { value: 'active', label: 'Aktywne' },
  { value: 'disabled', label: 'Wyłączone' },
  { value: 'deleted', label: 'Usunięte' },
]

function KlasaDetailPage() {
  const { klasaId } = Route.useParams()
  const id = Number(klasaId)
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [lifecycle, setLifecycle] = useState<null | 'archive' | 'restore'>(null)
  const [deleting, setDeleting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const query = useV1ClassesRetrieve(id)
  const klass = query.data?.status === 200 ? query.data.data : undefined

  const meQuery = useV1MeRetrieve()
  const role = meQuery.data?.status === 200 ? meQuery.data.data.role : undefined
  const isSuperadmin = role === 'superadmin'
  const isOperator = role === 'superadmin' || role === 'admin'

  const archiveMutation = useV1ClassesArchiveCreate()
  const restoreMutation = useV1ClassesRestoreCreate()
  const deleteMutation = useV1ClassesDeleteCreate()
  const bulkDisableMutation = useV1ClassesBulkDisableCreate()
  const cascadeMutation = useV1ClassesBulkCascadeDeleteCreate()
  const lifecyclePending = archiveMutation.isPending || restoreMutation.isPending

  const invalidateLists = async () => {
    await queryClient.invalidateQueries({ queryKey: getV1ClassesListQueryKey() })
    // Archiving/deleting a class changes the owning school's class counts.
    await queryClient.invalidateQueries({ queryKey: getV1SchoolsListQueryKey() })
  }

  const runLifecycle = async () => {
    setActionError(null)
    const mutation = lifecycle === 'archive' ? archiveMutation : restoreMutation
    const response = await mutation.mutateAsync({ id })
    if (response.status >= 400) {
      setActionError('Nie udało się zmienić stanu klasy. Spróbuj ponownie.')
      return
    }
    await queryClient.invalidateQueries({ queryKey: getV1ClassesRetrieveQueryKey(id) })
    await invalidateLists()
    setLifecycle(null)
  }

  const runDelete = async () => {
    if (!klass) return
    setActionError(null)
    const response = await deleteMutation.mutateAsync({ id, data: { name: klass.name } })
    if (response.status >= 400) {
      setActionError('Nie udało się usunąć klasy. Spróbuj ponownie.')
      return
    }
    await invalidateLists()
    setDeleting(false)
    navigate({ to: '/klasy' })
  }

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="Klasa" />
        <p className="p-6 text-sm text-muted-foreground">Ładowanie…</p>
      </>
    )
  }
  if (!klass) {
    return (
      <>
        <PageHeader title="Klasa" />
        <p className="p-6 text-sm text-muted-foreground">Nie znaleziono klasy.</p>
      </>
    )
  }

  if (editing && isOperator) {
    return (
      <>
        <PageHeader
          title={
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-4" /> {klass.name}
            </button>
          }
        />
        <div className="p-6">
          <h2 className="mb-6 text-lg font-semibold">Edycja klasy</h2>
          <ClassForm klass={klass} />
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon" aria-label="Wróć do listy klas">
              <Link to="/klasy">
                <ArrowLeft />
              </Link>
            </Button>
            Klasa {klass.name} ·{' '}
            {isOperator ? (
              <Link to="/szkoly/$szkolaId" params={{ szkolaId: String(klass.school) }}>
                {klass.school_name}
              </Link>
            ) : (
              <span>{klass.school_name}</span>
            )}
            <StatusBadge tone="outline">{klass.class_code_label}</StatusBadge>
            <StatusBadge tone={stateTone[klass.state]}>{klass.state_label}</StatusBadge>
          </span>
        }
        actions={
          <div className="flex gap-2">
            {isOperator && klass.state === 'active' && (
              <Button size="sm" variant="outline" onClick={() => setLifecycle('archive')}>
                <Archive /> Archiwizuj
              </Button>
            )}
            {isOperator && klass.state === 'archived' && (
              <Button size="sm" variant="outline" onClick={() => setLifecycle('restore')}>
                <RotateCcw /> Przywróć
              </Button>
            )}
            {isSuperadmin && (
              <Button size="sm" variant="destructive" onClick={() => setDeleting(true)}>
                <Trash2 /> Usuń
              </Button>
            )}
            {isOperator && klass.state === 'active' && (
              <Button size="sm" onClick={() => setEditing(true)}>
                <Pencil /> Edytuj
              </Button>
            )}
          </div>
        }
      />

      <ConfirmDialog
        open={lifecycle === 'archive'}
        onOpenChange={(open) => {
          if (!open) {
            setLifecycle(null)
            setActionError(null)
          }
        }}
        title="Archiwizacja klasy"
        description={
          <span className="flex flex-col gap-2">
            <span>Po archiwizacji klasy:</span>
            <span className="flex flex-col gap-1 text-sm">
              <span>
                • zniknie z domyślnego widoku listy klas (pozostanie pod filtrem statusu),
              </span>
              <span>• kod klasowy przestanie umożliwiać rejestrację nowych uczniów,</span>
              <span>• dane klasy i historia uczniów zostaną zachowane — nic nie jest usuwane,</span>
              <span>• operacja jest odwracalna — klasę można przywrócić.</span>
            </span>
          </span>
        }
        confirmLabel="Archiwizuj"
        pending={lifecyclePending}
        error={lifecycle === 'archive' ? actionError : null}
        onConfirm={runLifecycle}
      />

      <ConfirmDialog
        open={lifecycle === 'restore'}
        onOpenChange={(open) => {
          if (!open) {
            setLifecycle(null)
            setActionError(null)
          }
        }}
        title="Przywrócenie klasy"
        description="Klasa wróci do stanu „aktywna” i ponownie pojawi się w domyślnym widoku listy."
        confirmLabel="Przywróć"
        pending={lifecyclePending}
        error={lifecycle === 'restore' ? actionError : null}
        onConfirm={runLifecycle}
      />

      <DeleteClassDialog
        open={deleting}
        onOpenChange={(open) => {
          setDeleting(open)
          if (!open) setActionError(null)
        }}
        className={klass.name}
        pending={deleteMutation.isPending}
        error={deleting ? actionError : null}
        onConfirm={runDelete}
      />
      <div className="space-y-4 p-6">
        <Card>
          <CardContent className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-5">
            {(
              [
                ['Rok startu', String(klass.program_start_year)],
                ['Profil', klass.profile || '—'],
                ['Kod klasowy', klass.class_code_label],
                ['Data dołączenia', klass.joined_program_on],
                ['Liczba uczniów', klass.student_count == null ? '—' : String(klass.student_count)],
              ] as const
            ).map(([label, value]) => (
              <div key={label}>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="mt-0.5 font-medium">{value}</p>
              </div>
            ))}
            {isOperator && (
              <div className="col-span-2 sm:col-span-5">
                <p className="text-xs text-muted-foreground">Notatki administracyjne</p>
                <p className="mt-0.5 whitespace-pre-wrap">{klass.admin_notes || '—'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {isOperator && klass.state !== 'deleted' && (
          <BulkScopeActions
            scopeKind="class"
            scopeId={klass.id}
            scopeName={klass.name}
            schoolId={klass.school}
            canMassDisable={isOperator}
            canCascade={isSuperadmin}
            massDisableAvailable={klass.state === 'active'}
            cascadeAvailable={klass.state === 'active' || klass.state === 'archived'}
            onInitiateMassDisable={() => bulkDisableMutation.mutateAsync({ id })}
            onInitiateCascade={(data) => cascadeMutation.mutateAsync({ id, data })}
            onCascadeCompleted={async () => {
              await queryClient.invalidateQueries({ queryKey: getV1ClassesRetrieveQueryKey(id) })
              await invalidateLists()
            }}
          />
        )}

        {isOperator && (
          <ClassCoordinatorsCard
            classId={klass.id}
            classActive={klass.state === 'active'}
            coordinators={klass.coordinators}
          />
        )}

        {isOperator && (
          <ModuleConfigCard
            level="class"
            targetId={klass.id}
            targetState={klass.state}
            role={role}
          />
        )}

        <StudentAccountsSection
          classId={klass.id}
          className={klass.name}
          isOpenProgram={klass.school_is_open_program}
          active={klass.is_operationally_active}
          role={role}
        />
      </div>
    </>
  )
}

/**
 * Student-account management for a class (TR-800/802/803): roster, single-account
 * creation, batch/secondary credential sheets with one-time download, block/unblock,
 * and teacher-assisted password reset (TR-1000, feature 015). The public student identity is
 * the immutable login; the current `display_code` wire alias is retained only for API compatibility.
 */
export function StudentAccountsSection({
  classId,
  className,
  isOpenProgram,
  active,
  role,
}: {
  classId: number
  className: string
  isOpenProgram: boolean
  active: boolean
  role: Me['role'] | undefined
}) {
  const queryClient = useQueryClient()
  const isOperator = role === 'superadmin' || role === 'admin'
  const isSuperadmin = role === 'superadmin'
  // TR-1100/1101 archival state axis. Scoped, mutually-exclusive tabs (feature 011):
  // the roster row status is only active|blocked on the wire — a disabled account reads as
  // 'blocked' — so a mixed view can't label disabled rows. Each tab shows one population,
  // and the row's lifecycle state is read from `stateFilter`, not the ambiguous row status.
  const [stateFilter, setStateFilter] = useState<StudentRosterStateFilter>('active')
  const showingActive = stateFilter === 'active'
  const showingDisabled = stateFilter === 'disabled'
  const showingDeleted = stateFilter === 'deleted'

  const [page, setPage] = useState(1)
  const rosterParams = {
    status: 'all' as const,
    state: stateFilter === 'active' ? undefined : V1ClassesStudentsRetrieveState[stateFilter],
    page,
    page_size: ROSTER_PAGE_SIZE,
  }
  const roster = useV1ClassesStudentsRetrieve(classId, rosterParams, {
    query: { placeholderData: keepPreviousPage(rosterParams) },
  })
  const rosterData = roster.data?.status === 200 ? roster.data.data : undefined
  const rows: StudentRosterRow[] = rosterData?.results ?? []
  const totalCount = rosterData?.count ?? 0
  const pendingSheet = rosterData?.pending_sheet ?? null
  const notActivated = rosterData?.not_activated_count ?? 0
  const lastPage = Math.max(1, Math.ceil(totalCount / ROSTER_PAGE_SIZE))
  // Po usunięciu ostatniego wiersza bieżąca strona przestaje istnieć.
  if (rosterData && !roster.isPlaceholderData && page > lastPage) setPage(lastPage)

  const [batchOpen, setBatchOpen] = useState(false)
  const [batchCount, setBatchCount] = useState(30)
  const [batchFormat, setBatchFormat] = useState<'pdf' | 'xls'>('pdf')
  const [secondaryOpen, setSecondaryOpen] = useState(false)
  const [secondaryFormat, setSecondaryFormat] = useState<'pdf' | 'xls'>('pdf')
  const [singleOpen, setSingleOpen] = useState(false)
  const [credentials, setCredentials] = useState<Credentials | null>(null)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  // Roster row whose module overrides are open in the per-student dialog (TR-1200 R13).
  const [modulesRow, setModulesRow] = useState<StudentRosterRow | null>(null)
  // TR-1100/1101 lifecycle dialogs (feature 011), each keyed on the target row.
  const [disableRow, setDisableRow] = useState<StudentRosterRow | null>(null)
  const [restoreRow, setRestoreRow] = useState<StudentRosterRow | null>(null)
  const [deleteRow, setDeleteRow] = useState<StudentRosterRow | null>(null)
  const [deleteTyped, setDeleteTyped] = useState('')
  // TR-605 transfer (feature 013), superadmin-only, keyed on the target row.
  const [transferRow, setTransferRow] = useState<StudentRosterRow | null>(null)
  const [transferError, setTransferError] = useState<string | null>(null)
  // TR-1000 teacher-assisted reset by immutable login (feature 015). `resetWarning` holds the coarse activity
  // bucket while the R12 warning dialog is open for `resetRow`; null = dialog closed.
  const [resetRow, setResetRow] = useState<StudentRosterRow | null>(null)
  const [resetWarning, setResetWarning] = useState<ActivityEnum | null>(null)

  const singleMutation = useV1ClassesStudentsCreate()
  const sheetCreateMutation = useV1ClassesStudentsSheetCreate()
  const sheetCancelMutation = useV1ClassesStudentsSheetDestroy()
  const blockMutation = useV1ClassesStudentsBlockCreate()
  const unblockMutation = useV1ClassesStudentsUnblockCreate()
  const disableMutation = useV1ClassesStudentsDisableCreate()
  const restoreMutation = useV1ClassesStudentsRestoreCreate()
  const transferMutation = useV1ClassesStudentsTransferCreate()
  const resetMutation = useV1StudentsPasswordResetCreate({ mutation: { gcTime: 0 } })
  const [deletePending, setDeletePending] = useState(false)

  const invalidateRoster = () =>
    queryClient.invalidateQueries({ queryKey: getV1ClassesStudentsRetrieveQueryKey(classId) })

  const generateSingle = async () => {
    setActionError(null)
    const response = await singleMutation.mutateAsync({ id: classId })
    if (response.status === 409) {
      setActionError('Konta można dodać tylko do aktywnej klasy w aktywnej szkole.')
      return
    }
    if (response.status !== 201) {
      setActionError('Nie udało się utworzyć konta. Spróbuj ponownie.')
      return
    }
    setSingleOpen(false)
    setCredentials({
      login: studentLogin(response.data),
      password: response.data.temporary_password,
    })
    await invalidateRoster()
  }

  // TR-1000 two-step reset. `confirm=false` first: a recently-active account comes back as a
  // warning (no reset performed) and opens the R12 dialog; the operator's "Kontynuuj reset"
  // re-runs with `confirm=true`. A "reset" outcome (either step) surfaces the one-time
  // credentials popup. `row` is threaded so a 409 can pick copy from the row's lifecycle state.
  const runReset = async (row: StudentRosterRow, answer: string, confirm: boolean) => {
    setActionError(null)
    const login = studentLoginValue(row)
    if (!login) {
      setActionError(resetErrorCopy.technical)
      return
    }
    const response = await resetMutation.mutateAsync({
      data: { ...studentLoginWireFields(login), answer, confirm },
    })
    if (response.status === 200) {
      if (response.data.outcome === 'warning') {
        setResetRow(row)
        setResetWarning(response.data.activity)
        return
      }
      // outcome: 'reset' — populate the shared one-time popup, then close the warning flow.
      setResetRow(null)
      setResetWarning(null)
      resetMutation.reset()
      try {
        setCredentials({
          login: studentLogin(response.data),
          password: response.data.temporary_password,
        })
      } catch {
        // The reset already committed server-side but the one-time password could not be
        // surfaced; it is unrecoverable, so a fresh reset is required (FR-010 last bullet).
        setActionError(resetErrorCopy.popupLost)
      }
      await invalidateRoster()
      return
    }
    if (response.status === 400 || response.status === 429 || response.status === 409) {
      setActionError(
        response.data && 'detail' in response.data && typeof response.data.detail === 'string'
          ? response.data.detail
          : resetErrorCopy.notActive,
      )
      return
    }
    if (response.status === 404) {
      setActionError(resetErrorCopy.notFound)
    } else if (response.status === 410) {
      setActionError(resetErrorCopy.gone)
      await invalidateRoster()
    } else {
      setActionError(resetErrorCopy.technical)
    }
  }

  const generateSheet = async (kind: 'pack' | 'secondary', format: 'pdf' | 'xls') => {
    setActionError(null)
    const data = kind === 'pack' ? { kind, format, count: batchCount } : { kind, format }
    const response = await sheetCreateMutation.mutateAsync({ id: classId, data })
    if (response.status === 409) {
      setActionError(
        kind === 'pack'
          ? 'Klasa ma już niepobrany arkusz albo nie jest aktywna. Najpierw pobierz lub anuluj poprzedni.'
          : 'Klasa ma już niepobrany arkusz. Najpierw pobierz lub anuluj poprzedni.',
      )
      return
    }
    if (response.status >= 400) {
      setActionError('Nie udało się wygenerować arkusza. Spróbuj ponownie.')
      return
    }
    setBatchOpen(false)
    setSecondaryOpen(false)
    await invalidateRoster()
  }

  const downloadSheet = async () => {
    setActionError(null)
    setDownloading(true)
    try {
      const response = await v1ClassesStudentsSheetRetrieve(classId)
      if (response.status !== 200) {
        setActionError('Arkusz już pobrany lub niedostępny. Odśwież widok.')
        return
      }
      const disposition = response.headers.get('content-disposition') ?? ''
      const match = /filename="?([^"]+)"?/.exec(disposition)
      const filename = match?.[1] ?? `arkusz-kont-${classId}`
      const url = URL.createObjectURL(response.data)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
      await invalidateRoster()
    } finally {
      setDownloading(false)
    }
  }

  const cancelSheet = async () => {
    setActionError(null)
    const response = await sheetCancelMutation.mutateAsync({ id: classId })
    if (response.status >= 400) {
      setActionError('Nie udało się anulować arkusza. Spróbuj ponownie.')
      return
    }
    setCancelOpen(false)
    await invalidateRoster()
  }

  const toggleBlock = async (row: StudentRosterRow) => {
    setActionError(null)
    const response =
      row.status === 'blocked'
        ? await unblockMutation.mutateAsync({ id: classId, membershipPk: String(row.id) })
        : await blockMutation.mutateAsync({ id: classId, membershipPk: String(row.id), data: {} })
    if (response.status >= 400) {
      setActionError('Nie udało się zmienić stanu konta. Spróbuj ponownie.')
      return
    }
    await invalidateRoster()
  }

  // TR-1100 disable: active → disabled. 400 = precondition (usually blocked-must-unblock).
  const runDisable = async () => {
    if (!disableRow) return
    setActionError(null)
    const response = await disableMutation.mutateAsync({
      id: classId,
      membershipPk: String(disableRow.id),
    })
    if (response.status === 400) {
      setActionError('Konta zablokowanego nie można wyłączyć — najpierw je odblokuj.')
      return
    }
    if (response.status >= 400) {
      setActionError('Nie udało się wyłączyć konta. Spróbuj ponownie.')
      return
    }
    setDisableRow(null)
    await invalidateRoster()
  }

  // TR-1100 restore: disabled → active. 400 = not disabled or class/school inactive.
  const runRestore = async (forcePasswordChange: boolean) => {
    if (!restoreRow) return
    setActionError(null)
    const response = await restoreMutation.mutateAsync({
      id: classId,
      membershipPk: String(restoreRow.id),
      data: { force_password_change: forcePasswordChange },
    })
    if (response.status === 400) {
      setActionError(
        'Nie można przywrócić: konto nie jest wyłączone lub jego klasa/szkoła nie jest aktywna.',
      )
      return
    }
    if (response.status >= 400) {
      setActionError('Nie udało się przywrócić konta. Spróbuj ponownie.')
      return
    }
    setRestoreRow(null)
    await invalidateRoster()
  }

  // TR-1101 delete (anonymize). The generated client takes the typed control-text body
  // (see specs/011 research R2); 204 = done, 400 = control-text mismatch,
  // 409 = pending credential sheet, 404 = absent.
  const runDelete = async () => {
    if (!deleteRow) return
    setActionError(null)
    setDeletePending(true)
    try {
      const response = await v1ClassesStudentsDestroy(classId, String(deleteRow.id), {
        control_text: deleteTyped,
      })
      if (response.status === 409) {
        setActionError('Najpierw pobierz lub anuluj arkusz poświadczeń tej klasy.')
        return
      }
      if (response.status === 400) {
        setActionError('Tekst kontrolny nie zgadza się z kodem ucznia.')
        return
      }
      if (response.status >= 400) {
        setActionError('Nie udało się usunąć konta. Spróbuj ponownie.')
        return
      }
      setDeleteRow(null)
      setDeleteTyped('')
      await invalidateRoster()
    } finally {
      setDeletePending(false)
    }
  }

  // TR-605 transfer (feature 038 endpoint): move the student to another class with the
  // operator-chosen journal number. 400 = precondition or a number taken in a race (the client
  // pre-checks against the preview's occupied set, so this only fires on a race or stale state);
  // on success the student leaves this roster, so both classes' rosters and the class list are
  // invalidated. Success is signalled by closing + refresh (the house pattern — no toast lib).
  const runTransfer = async (targetClassId: number, journalNumber: number) => {
    if (!transferRow) return
    setTransferError(null)
    const response = await transferMutation.mutateAsync({
      id: classId,
      membershipPk: String(transferRow.id),
      data: { target_class_id: targetClassId, journal_number: journalNumber },
    })
    if (response.status === 400) {
      setTransferError(
        'Nie udało się przenieść: numer w dzienniku jest już zajęty w klasie docelowej lub zmieniły się warunki (klasa/konto nieaktywne). Sprawdź numer i spróbuj ponownie.',
      )
      return
    }
    if (response.status >= 400) {
      setTransferError('Nie udało się przenieść ucznia. Spróbuj ponownie.')
      return
    }
    setTransferRow(null)
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getV1ClassesStudentsRetrieveQueryKey(classId),
      }),
      queryClient.invalidateQueries({
        queryKey: getV1ClassesStudentsRetrieveQueryKey(targetClassId),
      }),
      queryClient.invalidateQueries({ queryKey: getV1ClassesListQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getV1ClassesRetrieveQueryKey(classId) }),
    ])
  }

  const sheetPending = sheetCreateMutation.isPending
  const togglePending = blockMutation.isPending || unblockMutation.isPending

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Konta uczniów</h2>
        {/* Credential-sheet + create actions target the active roster; hide them while
            browsing the archival (Wyłączone / Usunięte) views where they'd be meaningless
            — notActivated comes from the filtered set. */}
        {showingActive && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!active || notActivated === 0 || !!pendingSheet || sheetPending}
              title="Arkusz dla kont nieaktywowanych"
              onClick={() => setSecondaryOpen(true)}
            >
              <FileSpreadsheet /> Wtórny arkusz
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!active || singleMutation.isPending}
              onClick={() => setSingleOpen(true)}
            >
              <UserPlus /> Dodaj konto ucznia
            </Button>
            <Button
              size="sm"
              onClick={() => setBatchOpen(true)}
              disabled={!active || !!pendingSheet || sheetPending}
            >
              <Plus /> Wygeneruj paczkę kont
            </Button>
          </div>
        )}
      </div>

      {actionError && <p className="text-sm text-destructive">{actionError}</p>}

      {showingActive && pendingSheet && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-3.5 dark:border-amber-900 dark:bg-amber-950">
          <TriangleAlert className="size-4 shrink-0 text-amber-600" />
          <p className="flex-1 text-sm text-amber-800 dark:text-amber-200">
            {pendingSheet.kind === 'pack' ? 'Paczka kont' : 'Wtórny arkusz'} oczekuje na pobranie (
            {pendingSheet.account_count} kont). Pobranie jest{' '}
            <span className="font-medium">jednorazowe</span> — po nim plik znika z systemu.
          </p>
          <Button variant="outline" size="sm" onClick={downloadSheet} disabled={downloading}>
            <Download /> Pobierz arkusz
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-amber-700"
            onClick={() => setCancelOpen(true)}
          >
            Anuluj
          </Button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border bg-background">
        <div className="flex items-center justify-between gap-4 border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold">
              Uczniowie{showingDeleted ? '' : ` (${totalCount})`}
            </h3>
            {/* The archival state views (Wyłączone / Usunięte) belong to the operator-only
                disable/delete module (TR-1100/1101 R1) — teachers only ever see the default
                active roster, so the filter itself is operator-gated. */}
            {isOperator && (
              <Select
                value={stateFilter}
                onValueChange={(v) => {
                  setStateFilter(v as StudentRosterStateFilter)
                  setPage(1)
                  setActionError(null)
                }}
              >
                <SelectTrigger size="sm" className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {rosterStateOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Login ucznia jest jego publicznym, niezmiennym identyfikatorem.
          </p>
        </div>
        {roster.isLoading ? (
          <p className="px-5 py-6 text-sm text-muted-foreground">Ładowanie…</p>
        ) : showingDeleted ? (
          <p className="px-5 py-6 text-sm text-muted-foreground">
            Konta usunięte są anonimizowane i nie mają wiersza na liście klasy — pozostają widoczne
            wyłącznie pod swoim identyfikatorem w archiwalnym widoku audytu.
          </p>
        ) : rows.length === 0 ? (
          <p className="px-5 py-6 text-sm text-muted-foreground">
            {showingDisabled
              ? 'Brak wyłączonych kont w tej klasie.'
              : 'Klasa nie ma jeszcze żadnych kont uczniów. Wygeneruj paczkę lub dodaj konto.'}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Login</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Ostatnie logowanie</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="group">
                  <TableCell className="font-mono text-[13px] font-medium">
                    <span className="flex items-center gap-2.5">
                      {/* alt pusty: login ucznia stoi obok i niesie tę samą tożsamość. */}
                      <AvatarPortrait
                        options={r.avatar}
                        seed={avatarSeed(r.display_code)}
                        alt=""
                        size="sm"
                      />
                      {studentLogin(r)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1.5">
                      {showingDisabled ? (
                        <StatusBadge tone="zinc">wyłączony</StatusBadge>
                      ) : (
                        <>
                          {r.status === 'active' && <StatusBadge tone="green">aktywny</StatusBadge>}
                          {r.status === 'blocked' && (
                            <StatusBadge tone="red">zablokowany</StatusBadge>
                          )}
                        </>
                      )}
                      {r.password_change_required && (
                        <StatusBadge tone="amber">
                          <KeyRound className="size-3" /> wymagana zmiana hasła
                        </StatusBadge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {r.last_login ?? <span className="opacity-50">nieaktywowane</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      {isOperator && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Moduły ucznia"
                          onClick={() => setModulesRow(r)}
                        >
                          <Layers />
                        </Button>
                      )}
                      {showingDisabled ? (
                        isOperator && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Przywróć konto"
                              onClick={() => setRestoreRow(r)}
                            >
                              <RotateCcw className="text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Usuń konto"
                              onClick={() => {
                                setDeleteTyped('')
                                setDeleteRow(r)
                              }}
                            >
                              <Trash2 className="text-red-500" />
                            </Button>
                          </>
                        )
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={togglePending}
                            title={r.status === 'blocked' ? 'Odblokuj konto' : 'Zablokuj konto'}
                            onClick={() => toggleBlock(r)}
                          >
                            {r.status === 'blocked' ? (
                              <LockOpen className="text-green-600" />
                            ) : (
                              <Ban className="text-red-500" />
                            )}
                          </Button>
                          {isSuperadmin && r.status === 'active' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Przenieś do innej klasy"
                              onClick={() => {
                                setTransferError(null)
                                setTransferRow(r)
                              }}
                            >
                              <ArrowLeftRight className="text-sky-600" />
                            </Button>
                          )}
                          {(isOperator || role === 'teacher') && r.status === 'active' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Resetuj hasło"
                              disabled={resetMutation.isPending}
                              onClick={() => {
                                setActionError(null)
                                setResetWarning(null)
                                setResetRow(r)
                              }}
                            >
                              <KeyRound className="text-sky-600" />
                            </Button>
                          )}
                          {isOperator && r.status === 'active' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Wyłącz konto"
                              onClick={() => setDisableRow(r)}
                            >
                              <Power className="text-amber-600" />
                            </Button>
                          )}
                          {isOperator && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Usuń konto"
                              onClick={() => {
                                setDeleteTyped('')
                                setDeleteRow(r)
                              }}
                            >
                              <Trash2 className="text-red-500" />
                            </Button>
                          )}
                        </>
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {!showingDeleted && (
          <Pager
            page={page}
            count={totalCount}
            pageSize={ROSTER_PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </div>

      <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Wygeneruj paczkę kont</DialogTitle>
            <DialogDescription>Klasa {className}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="batch-count">Liczba kont</Label>
              <Input
                id="batch-count"
                type="number"
                min={1}
                max={60}
                value={batchCount}
                onChange={(e) => setBatchCount(Number(e.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Format arkusza</Label>
              <RadioGroup
                value={batchFormat}
                onValueChange={(v) => setBatchFormat(v as 'pdf' | 'xls')}
                className="flex gap-4"
              >
                <label className="flex items-center gap-2 text-sm">
                  <RadioGroupItem value="pdf" /> PDF
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <RadioGroupItem value="xls" /> XLS
                </label>
              </RadioGroup>
            </div>
            <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              Wszystkie konta powstają w jednej transakcji. Arkusz z danymi do logowania będzie
              dostępny do jednorazowego pobrania.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchOpen(false)}>
              Anuluj
            </Button>
            <Button onClick={() => generateSheet('pack', batchFormat)} disabled={sheetPending}>
              Wygeneruj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={secondaryOpen} onOpenChange={setSecondaryOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Wtórny arkusz kont nieaktywowanych</DialogTitle>
            <DialogDescription>
              Nada nowe hasła tymczasowe {notActivated} kontom, które nie ukończyły pierwszego
              logowania. Dotychczasowe hasła tymczasowe tych kont przestaną działać; login pozostaje
              bez zmian.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Format arkusza</Label>
            <RadioGroup
              value={secondaryFormat}
              onValueChange={(v) => setSecondaryFormat(v as 'pdf' | 'xls')}
              className="flex gap-4"
            >
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="pdf" /> PDF
              </label>
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="xls" /> XLS
              </label>
            </RadioGroup>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSecondaryOpen(false)}>
              Anuluj
            </Button>
            <Button
              onClick={() => generateSheet('secondary', secondaryFormat)}
              disabled={sheetPending}
            >
              Wygeneruj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={singleOpen} onOpenChange={setSingleOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Dodaj konto ucznia</DialogTitle>
            <DialogDescription>
              Wygenerować nowe konto ucznia dla klasy {className}? Dane do pierwszego logowania
              zostaną pokazane jednorazowo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSingleOpen(false)}>
              Anuluj
            </Button>
            <Button onClick={generateSingle} disabled={singleMutation.isPending}>
              Wygeneruj konto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!credentials} onOpenChange={(open) => !open && setCredentials(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nowe dane logowania</DialogTitle>
          </DialogHeader>
          {credentials && (
            <>
              <div className="rounded-lg bg-gray-900 px-4 py-3 font-mono text-sm text-gray-100">
                {(
                  [
                    ['Login', credentials.login],
                    ['Hasło tymczasowe', credentials.password],
                  ] as const
                ).map(([label, value]) => (
                  <div key={label} className="flex justify-between py-1">
                    <span className="text-gray-400">{label}</span>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2.5 dark:bg-amber-950">
                <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  Hasło jest widoczne tylko teraz — po zamknięciu okna nie będzie dostępne. Uczeń
                  powinien odczytać dane z ekranu lub je zapisać. Przy pierwszym logowaniu system
                  wymusi zmianę hasła.
                </p>
              </div>
              {credentials.expiresAt && (
                <p className="text-sm text-muted-foreground">
                  Hasło jest ważne do: <strong>{formatExpiry(credentials.expiresAt)}</strong>. Po
                  zalogowaniu uczeń zmieni hasło i ustawi pytanie bezpieczeństwa od nowa.
                </p>
              )}
            </>
          )}
          <DialogFooter>
            <Button onClick={() => setCredentials(null)}>Zamknij</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Anuluj arkusz</DialogTitle>
            <DialogDescription>
              {pendingSheet?.kind === 'pack'
                ? `Anulowanie usunie ${pendingSheet.account_count} niepobranych kont oraz arkusz. Tej operacji nie można cofnąć.`
                : 'Anulowanie usunie arkusz. Konta zachowają nowe hasła tymczasowe (wartości jawne przepadną — dostęp przez kolejny wtórny arkusz).'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>
              Wróć
            </Button>
            <Button
              variant="destructive"
              onClick={cancelSheet}
              disabled={sheetCancelMutation.isPending}
            >
              Anuluj arkusz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!disableRow}
        onOpenChange={(open) => {
          if (!open) {
            setDisableRow(null)
            setActionError(null)
          }
        }}
        title="Wyłączenie konta ucznia"
        description={
          <span className="flex flex-col gap-2">
            <span>Po wyłączeniu konta{disableRow ? ` ${studentLogin(disableRow)}` : ''}:</span>
            <span className="flex flex-col gap-1 text-sm">
              <span>• uczeń zostanie natychmiast wylogowany ze wszystkich urządzeń,</span>
              <span>• logowanie na to konto będzie niemożliwe do czasu przywrócenia,</span>
              <span>• wszystkie dane konta (punkty, postępy, odznaki) zostaną zachowane,</span>
              <span>• operacja jest odwracalna — konto można przywrócić z widoku „Wyłączone”.</span>
            </span>
          </span>
        }
        confirmLabel="Wyłącz"
        destructive
        pending={disableMutation.isPending}
        error={disableRow ? actionError : null}
        onConfirm={runDisable}
      />

      <RestoreStudentDialog
        open={!!restoreRow}
        onOpenChange={(open) => {
          if (!open) {
            setRestoreRow(null)
            setActionError(null)
          }
        }}
        login={restoreRow ? studentLogin(restoreRow) : ''}
        lastLogin={restoreRow?.last_login ?? null}
        pending={restoreMutation.isPending}
        error={restoreRow ? actionError : null}
        onConfirm={runRestore}
      />

      <DeleteStudentDialog
        open={!!deleteRow}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteRow(null)
            setDeleteTyped('')
            setActionError(null)
          }
        }}
        login={deleteRow ? studentLogin(deleteRow) : ''}
        typed={deleteTyped}
        onTypedChange={setDeleteTyped}
        pending={deletePending}
        error={deleteRow ? actionError : null}
        onConfirm={runDelete}
      />

      <TransferStudentDialog
        open={!!transferRow}
        onOpenChange={(open) => {
          if (!open) {
            setTransferRow(null)
            setTransferError(null)
          }
        }}
        row={transferRow}
        sourceClassId={classId}
        sourceClassName={className}
        sourceIsOpenProgram={isOpenProgram}
        pending={transferMutation.isPending}
        error={transferError}
        onConfirm={runTransfer}
      />

      {resetRow && (
        <ResetPasswordDialog
          open
          onOpenChange={(open) => {
            // "Anuluj" (or backdrop close): no mutation, account untouched (FR-005).
            if (!open) {
              setResetRow(null)
              setResetWarning(null)
              setActionError(null)
              resetMutation.reset()
            }
          }}
          login={studentLogin(resetRow)}
          activity={resetWarning}
          pending={resetMutation.isPending}
          error={actionError}
          onConfirm={(answer, confirm) => runReset(resetRow, answer, confirm)}
          isSuperadmin={isSuperadmin}
          onOverride={(result) => {
            setResetRow(null)
            setResetWarning(null)
            setActionError(null)
            setCredentials({
              login: result.login,
              password: result.temporaryPassword,
              expiresAt: result.expiresAt,
            })
            void invalidateRoster()
          }}
          onSessionEnded={() => window.location.assign('/login?expired=ok')}
        />
      )}

      <StudentModulesDialog
        classId={classId}
        row={modulesRow}
        role={role}
        onOpenChange={(open) => {
          if (!open) setModulesRow(null)
        }}
      />
    </>
  )
}
